// Stackly Office Design System — components in one file for clarity.
// Direction C "Control Room". Follow /app/memory/DESIGN.md.
import React, { useEffect, useState } from "react";
import { X as CloseIcon } from "lucide-react";
import { AgentPortrait } from "./AgentPortrait";

export { AgentPortrait };

/* ---------- PageHeader ---------- */
function dubaiClock() {
  const now = new Date();
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Dubai",
    weekday: "short", day: "numeric", month: "short",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(now);
  const map = Object.fromEntries(fmt.map((p) => [p.type, p.value]));
  const time = `${map.hour}:${map.minute}`;
  const date = `${map.weekday}, ${map.day} ${map.month}`;
  return { time, date };
}

export function PageHeader({ title, subtitle, right }) {
  const [clock, setClock] = useState(dubaiClock);
  useEffect(() => {
    const t = setInterval(() => setClock(dubaiClock()), 15000);
    return () => clearInterval(t);
  }, []);
  return (
    <header className="flex items-start justify-between gap-6 pb-6">
      <div>
        <h1 className="font-ui text-[22px] font-bold tracking-tight text-[color:var(--fg)]" data-testid="page-title">
          {title}
        </h1>
        {subtitle && (
          <p className="text-sm text-[color:var(--muted)] mt-1">{subtitle}</p>
        )}
      </div>
      <div className="flex items-start gap-6">
        {right}
        <div className="text-right">
          <div className="font-mono text-2xl text-[color:var(--fg)] tabular-nums" data-testid="dubai-time">{clock.time}</div>
          <div className="font-mono text-xs text-[color:var(--muted)] tabular-nums">{clock.date} · Dubai</div>
        </div>
      </div>
    </header>
  );
}

/* ---------- Panel ---------- */
export function Panel({ label, action, className = "", children, testId }) {
  return (
    <section
      className={`rounded-[8px] border border-[color:var(--line)] bg-[color:var(--panel)] p-[14px] ${className}`}
      data-testid={testId}
    >
      {(label || action) && (
        <div className="flex items-center justify-between pb-3">
          {label && (
            <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-[color:var(--muted)]">
              {label}
            </span>
          )}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

/* ---------- Kpi ---------- */
export function Kpi({ label, value, sub, right, testId }) {
  return (
    <Panel testId={testId} className="min-h-[112px]">
      <div className="flex items-center justify-between pb-3">
        <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-[color:var(--muted)]">{label}</span>
        {right}
      </div>
      <div className="font-mono text-[28px] leading-none text-[color:var(--fg)] tabular-nums" data-testid={testId ? `${testId}-value` : undefined}>
        {value}
      </div>
      {sub && <div className="font-mono text-xs text-[color:var(--muted)] mt-3 tabular-nums">{sub}</div>}
    </Panel>
  );
}

/* ---------- Meter ---------- */
export function Meter({ value = 0, max = 100, testId }) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  const color =
    pct >= 100 ? "var(--bad)" : pct >= 80 ? "var(--warn)" : "var(--cobalt)";
  return (
    <div className="w-full h-[5px] rounded-full bg-[color:var(--line)] overflow-hidden" data-testid={testId}>
      <div className="h-full transition-[width]" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

/* ---------- StatusLight ---------- */
const STATUS_META = {
  working: { color: "var(--ok)", label: "WORKING" },
  idle:    { color: "var(--off)", label: "IDLE" },
  blocked: { color: "var(--bad)", label: "BLOCKED" },
  offline: { color: "var(--off)", label: "OFFLINE" },
  meeting: { color: "var(--violet)", label: "MEETING" },
};
export function StatusLight({ status = "idle", testId }) {
  const m = STATUS_META[status] || STATUS_META.idle;
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap" data-testid={testId}>
      <span
        className="inline-block w-[7px] h-[7px] rounded-full"
        style={{ background: m.color, boxShadow: `0 0 6px ${m.color}` }}
      />
      <span className="font-mono text-[10.5px] uppercase tracking-[0.1em]" style={{ color: m.color }}>
        {m.label}
      </span>
    </span>
  );
}

/* ---------- Chip ---------- */
const CHIP_COLORS = {
  draft: "var(--dim)",
  plan_requested: "var(--muted)",
  plan_ready: "var(--warn)",
  approved: "#7fb0ff",
  building: "var(--cobalt)",
  emergent_testing: "var(--violet)",
  acceptance_testing: "var(--violet)",
  qa: "var(--violet)",
  fix_requested: "var(--bad)",
  changes_requested: "var(--warn)",
  rejected: "var(--bad)",
  on_hold: "var(--off)",
  done: "var(--ok)",
  // priorities
  low: "var(--dim)",
  normal: "var(--muted)",
  high: "var(--warn)",
  urgent: "var(--bad)",
  // classifications / status
  spam: "var(--bad)",
  product_interest: "var(--ok)",
  support: "var(--cobalt)",
  custom_build: "var(--violet)",
  product_idea: "var(--warn)",
  partnership: "#7fb0ff",
  other: "var(--muted)",
  new: "var(--warn)",
  reviewed: "var(--cobalt)",
  replied: "var(--ok)",
  archived: "var(--dim)",
  // opportunity states
  opportunity: "var(--cobalt)",
  watching: "var(--warn)",
  dismissed: "var(--dim)",
  job_created: "var(--ok)",
};

export function Chip({ value, label, color, className = "", testId }) {
  const c = color || CHIP_COLORS[value] || "var(--muted)";
  return (
    <span
      className={`inline-flex items-center rounded-[4px] border px-[6px] py-[1px] font-mono text-[10.5px] uppercase tracking-[0.08em] ${className}`}
      style={{ color: c, borderColor: c }}
      data-testid={testId || (value ? `chip-${value}` : undefined)}
    >
      {label || (value ? String(value).replace(/_/g, " ") : "")}
    </span>
  );
}

/* ---------- Banner ---------- */
export function Banner({ label = "BUDGET", children, action, testId }) {
  return (
    <div
      className="rounded-[8px] border px-4 py-3 flex items-center gap-4"
      style={{
        background: "rgba(255, 107, 107, 0.08)",
        borderColor: "rgba(255, 107, 107, 0.4)",
        color: "var(--fg)",
      }}
      data-testid={testId}
    >
      <span className="font-mono text-[10.5px] uppercase tracking-[0.1em]" style={{ color: "var(--bad)" }}>
        {label}
      </span>
      <div className="flex-1 text-sm">{children}</div>
      {action}
    </div>
  );
}

/* ---------- Button ---------- */
const BTN_VARIANTS = {
  default:
    "border border-[color:var(--line-2)] bg-transparent text-[color:var(--fg)] hover:bg-[color:var(--cobalt-glow)]",
  primary:
    "border border-[color:var(--cobalt)] bg-[color:var(--cobalt)] text-white hover:brightness-110",
  go:
    "border border-[color:var(--ok)]/50 bg-[color:var(--ok)]/10 text-[color:var(--ok)] hover:bg-[color:var(--ok)]/20",
  no:
    "border border-[color:var(--bad)]/50 bg-[color:var(--bad)]/10 text-[color:var(--bad)] hover:bg-[color:var(--bad)]/20",
  ghost:
    "bg-transparent text-[color:var(--muted)] hover:text-[color:var(--fg)]",
};
export function DsButton({ variant = "default", className = "", children, ...rest }) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-[5px] px-3 h-9 text-[12.5px] font-semibold font-ui transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--cobalt)] disabled:opacity-50 disabled:pointer-events-none";
  return (
    <button className={`${base} ${BTN_VARIANTS[variant] || BTN_VARIANTS.default} ${className}`} {...rest}>
      {children}
    </button>
  );
}

/* ---------- DataTable ---------- */
export function DataTable({ columns, rows, onRowClick, empty = "No data.", testId }) {
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full">
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={`px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--muted)] text-left border-b border-[color:var(--line)] ${c.right ? "text-right" : ""}`}
                style={{ width: c.width }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-3 py-10 text-center text-sm text-[color:var(--muted)]">
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((r, i) => (
              <tr
                key={r.id || i}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                className={`border-b border-[color:var(--line)] ${onRowClick ? "cursor-pointer hover:bg-[color:var(--cobalt-glow)]" : ""}`}
                data-testid={r.id ? `row-${r.id}` : undefined}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`px-3 py-3 text-sm text-[color:var(--fg)] ${c.right ? "text-right font-mono tabular-nums" : ""} ${c.mono ? "font-mono tabular-nums text-[color:var(--muted)]" : ""}`}
                  >
                    {c.render ? c.render(r) : r[c.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Drawer (right side, 520px, Esc closes) ---------- */
export function Drawer({ open, onClose, children, testId = "drawer" }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50" data-testid={testId}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <aside
        className="absolute right-0 top-0 h-full w-[520px] max-w-full bg-[color:var(--panel)] border-l border-[color:var(--line)] shadow-2xl overflow-y-auto"
        style={{ animation: "ds-slide-in 160ms ease-out" }}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 inline-flex items-center gap-1.5 px-3 h-9 rounded-[5px] border border-[color:var(--line-2)] text-[color:var(--muted)] hover:text-[color:var(--fg)]"
          data-testid={`${testId}-close`}
        >
          <CloseIcon className="h-4 w-4" />
          <span className="text-xs">Close</span>
        </button>
        <div className="p-6 pt-5">{children}</div>
      </aside>
    </div>
  );
}

/* ---------- Timeline ---------- */
export function DsTimeline({ events = [], testId = "timeline" }) {
  if (!events.length) {
    return <div className="font-mono text-xs text-[color:var(--muted)]">No events yet.</div>;
  }
  return (
    <ol className="relative pl-5 space-y-3" data-testid={testId}>
      <span className="absolute left-[7px] top-1 bottom-1 w-px bg-[color:var(--line)]" />
      {events.map((e) => {
        const isError = e.event_type === "error";
        const dot = isError ? "var(--bad)" : "var(--cobalt)";
        return (
          <li key={e.id} className="relative" data-testid={`timeline-event-${e.id}`}>
            <span
              className="absolute -left-[18px] top-[6px] w-[9px] h-[9px] rounded-full"
              style={{ background: dot, boxShadow: `0 0 6px ${dot}` }}
            />
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-[10px] text-[color:var(--muted)] tabular-nums">
                {formatHM(e.created_at)}
              </span>
              <span className={`text-sm ${isError ? "text-[color:var(--bad)]" : "text-[color:var(--fg)]"}`}>
                {e.message || e.event_type}
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function formatHM(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const h = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Dubai", hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
  return h;
}

/* ---------- ChatBubble ---------- */
export function ChatBubble({ isCeo, agent, name, time, children, meta }) {
  if (isCeo) {
    return (
      <div className="flex justify-end">
        <div
          className="max-w-[70%] rounded-[12px] px-4 py-3 text-sm text-white"
          style={{ background: "var(--cobalt-deep)" }}
        >
          <div className="font-mono text-[10px] text-white/70 mb-1 tabular-nums">You · {time}</div>
          <div className="whitespace-pre-wrap break-words">{children}</div>
          {meta}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div
        className="max-w-[70%] rounded-[12px] px-4 py-3 text-sm flex gap-3 border"
        style={{ background: "var(--panel-2)", borderColor: "var(--line)", color: "var(--fg)" }}
      >
        {agent && <AgentPortrait agent={agent} size={28} />}
          <div>
            <div className="font-mono text-[10px] text-[color:var(--muted)] mb-1 tabular-nums">
              {name} · {time}
            </div>
            <div className="whitespace-pre-wrap break-words">{children}</div>
            {meta}
          </div>
      </div>
    </div>
  );
}

/* ---------- SpendChart ---------- */
function niceStep(roughStep) {
  if (roughStep <= 0) return 1;
  const exp = Math.floor(Math.log10(roughStep));
  const base = Math.pow(10, exp);
  const n = roughStep / base;
  const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return nice * base;
}
function fmtAxis(v) {
  if (v >= 1000) return `$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`;
  if (v >= 5) return `$${Math.round(v)}`;
  if (v >= 1) return `$${v.toFixed(1)}`;
  return `$${v.toFixed(2)}`;
}
function fmtPoint(v) {
  if (v >= 100) return `$${v.toFixed(0)}`;
  return `$${(v || 0).toFixed(2)}`;
}

export function SpendChart({ data = [], height = 220, testId = "spend-chart" }) {
  if (!data.length) {
    return <div className="text-sm text-[color:var(--muted)]">No data yet.</div>;
  }
  const width = 600;
  const padL = 48, padR = 32, padT = 10, padB = 24;
  const w = width - padL - padR;
  const h = height - padT - padB;

  const rawMax = Math.max(0, ...data.map((d) => Math.max(d.total || 0, d.claude || 0)));
  const step = niceStep(Math.max(rawMax, 1) / 4);
  const niceMax = Math.max(step, Math.ceil(rawMax / step) * step);
  const yTicks = [];
  for (let v = 0; v <= niceMax + 1e-9; v += step) yTicks.push(Number(v.toFixed(10)));

  const stepX = data.length > 1 ? w / (data.length - 1) : 0;
  const xy = (v, i) => [padL + i * stepX, padT + h - (v / niceMax) * h];
  const totalPath = data.map((d, i) => `${i === 0 ? "M" : "L"}${xy(d.total || 0, i).join(" ")}`).join(" ");
  const claudePath = data.map((d, i) => `${i === 0 ? "M" : "L"}${xy(d.claude || 0, i).join(" ")}`).join(" ");
  const area = totalPath
    ? `${totalPath} L${padL + (data.length - 1) * stepX} ${padT + h} L${padL} ${padT + h} Z`
    : "";
  const last = data[data.length - 1];
  const [lx, ly] = xy(last.total || 0, data.length - 1);
  // Place last-point value label above-left to avoid the line.
  const labelAbove = ly > padT + 16;
  const labelX = lx - 6;
  const labelY = labelAbove ? ly - 10 : ly + 14;

  return (
    <div className="w-full" data-testid={testId}>
      <div
        className="flex items-center justify-end gap-5 pb-2 font-mono text-[10.5px] uppercase tracking-[0.08em]"
        style={{ color: "var(--muted)" }}
        data-testid={`${testId}-legend`}
      >
        <span className="inline-flex items-center gap-2">
          <svg width="22" height="4" aria-hidden="true">
            <rect x="0" y="1" width="22" height="2" fill="var(--cobalt)" />
          </svg>
          Total (Claude + Emergent)
        </span>
        <span className="inline-flex items-center gap-2">
          <svg width="22" height="4" aria-hidden="true">
            <line x1="0" y1="2" x2="22" y2="2"
                  stroke="var(--ok)" strokeWidth="1.5" strokeDasharray="4 3" />
          </svg>
          Claude only
        </span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="ds-chart w-full h-auto block">
        {yTicks.map((v) => {
          const [, yy] = xy(v, 0);
          return (
            <g key={v}>
              <line x1={padL} y1={yy} x2={width - padR} y2={yy} className="ds-chart-grid" />
              <text x={padL - 6} y={yy + 3} fontSize="10"
                    className="ds-chart-axis" textAnchor="end"
                    fontFamily="ui-monospace, monospace">
                {fmtAxis(v)}
              </text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const [xx] = xy(0, i);
          const isLast = i === data.length - 1;
          const anchor = isLast ? "end" : (i === 0 ? "start" : "middle");
          const xAdj = isLast ? width - padR : (i === 0 ? padL : xx);
          return (
            <text key={i} x={xAdj} y={height - 6} fontSize="10"
                  className="ds-chart-date" textAnchor={anchor}
                  fontFamily="ui-monospace, monospace">
              {d.day}
            </text>
          );
        })}
        <path d={area} fill="var(--cobalt)" fillOpacity="0.14" />
        <path d={totalPath} stroke="var(--cobalt)" strokeWidth="2" fill="none" />
        <path d={claudePath} stroke="var(--ok)" strokeWidth="1.5"
              strokeDasharray="4 3" fill="none" />
        <circle cx={lx} cy={ly} r="3" fill="var(--cobalt)" />
        <text x={labelX} y={labelY} fontSize="11"
              className="ds-chart-label" textAnchor="end"
              fontFamily="ui-monospace, monospace" fontWeight="600">
          {fmtPoint(last.total || 0)}
        </text>
      </svg>
    </div>
  );
}

/* ---------- ApprovalCard ---------- */
export function ApprovalCard({ task, agent, onApprove, onChanges, onReject, onDetails, meta, testId }) {
  return (
    <div
      className="rounded-[7px] border border-[color:var(--line)] p-4"
      style={{ background: "var(--panel-2)" }}
      data-testid={testId || `approval-${task.id}`}
    >
      <div className="flex items-start gap-3">
        {agent && <AgentPortrait agent={agent} size={34} />}
        <div className="flex-1 min-w-0">
          <h3 className="font-ui text-base font-semibold text-[color:var(--fg)] truncate">{task.title}</h3>
          <div className="font-mono text-[11px] text-[color:var(--muted)] mt-0.5 flex gap-3 flex-wrap">
            {meta}
          </div>
        </div>
      </div>
      {task.plan?.summary && (
        <p
          className="text-sm text-[color:var(--muted)] mt-3 border-l-2 pl-3"
          style={{ borderColor: "var(--cobalt)" }}
        >
          {task.plan.summary}
        </p>
      )}
      <div className="flex gap-2 mt-4 flex-wrap">
        <DsButton variant="go" onClick={onApprove} data-testid={`${testId || task.id}-approve`}>
          Approve
        </DsButton>
        <DsButton variant="default" onClick={onChanges} data-testid={`${testId || task.id}-changes`}>
          Request changes
        </DsButton>
        <DsButton variant="no" onClick={onReject} data-testid={`${testId || task.id}-reject`}>
          Reject
        </DsButton>
        <DsButton variant="ghost" onClick={onDetails} data-testid={`${testId || task.id}-details`}>
          Details
        </DsButton>
      </div>
    </div>
  );
}
